import * as React from 'react';
import { ChevronLeft, Layers } from 'lucide-react';
import { cn } from '@hetivo/hetivo-lib-web-utils';
import { useTranslation } from 'react-i18next';
import { useMasterContext } from '&/master';
import { useLocations } from '../../../settings/locations/hooks/use-locations';
import { LocationRow } from './location-row';

// ─── Props ────────────────────────────────────────────────────────────────────

interface LocationsPanelProps {
  canReadLocations: boolean;
  canSelectTerritory: boolean;
  isSingleTerritoryImposed: boolean;
  onBack: () => void;
  onClose: () => void;
  territoryId: string | null;
  territoryName: string | null;
}

// ─── Private helpers ──────────────────────────────────────────────────────────

interface TerritoryHeaderProps {
  canSelectTerritory: boolean;
  isSelected: boolean;
  isSingleTerritoryImposed: boolean;
  onBack: () => void;
  onSelect: () => void;
  onSelectKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => void;
  territoryName: string | null;
}

function TerritoryHeader({
  canSelectTerritory,
  isSelected,
  isSingleTerritoryImposed,
  onBack,
  onSelect,
  onSelectKeyDown,
  territoryName,
}: TerritoryHeaderProps): React.JSX.Element {
  const { t } = useTranslation('common');

  if (!canSelectTerritory) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 border-y border-border">
        {!isSingleTerritoryImposed && (
          <button
            aria-label={t('mainSelector.back')}
            className="p-0.5 rounded hover:bg-accent transition-colors shrink-0 rtl:rotate-180"
            onClick={onBack}
            type="button"
          >
            <ChevronLeft className="size-4" />
          </button>
        )}
        <div className="flex items-center gap-2 flex-1 min-w-0 text-start">
          <Layers className="size-4 text-muted-foreground shrink-0" />
          <span className="text-sm font-medium truncate flex-1">{territoryName}</span>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'flex items-center gap-2 px-3 py-2 border-y border-border transition-colors group/territory-header cursor-pointer',
        isSelected ? 'bg-accent' : 'hover:bg-muted',
      )}
      onClick={onSelect}
      onKeyDown={onSelectKeyDown}
      role="button"
      tabIndex={0}
    >
      {/* Back button — hidden when territory is imposed (single-territory user) */}
      {!isSingleTerritoryImposed && (
        <button
          aria-label={t('mainSelector.back')}
          className="p-0.5 rounded hover:bg-accent transition-colors shrink-0 rtl:rotate-180"
          onClick={(e) => {
            e.stopPropagation();
            onBack();
          }}
          onKeyDown={(e) => e.stopPropagation()}
          type="button"
        >
          <ChevronLeft className="size-4" />
        </button>
      )}

      <div className="flex items-center gap-2 flex-1 min-w-0 text-start">
        <Layers className="size-4 text-muted-foreground shrink-0" />
        <span className="text-sm font-medium truncate flex-1">{territoryName}</span>
      </div>

      {isSelected ? (
        <span className="text-[9px] font-semibold text-muted-foreground bg-muted px-1.5 py-0.5 rounded shrink-0">
          {t('mainSelector.current')}
        </span>
      ) : (
        <span className="text-[11px] text-primary font-medium hidden group-hover/territory-header:inline shrink-0">
          {t('mainSelector.select')}
        </span>
      )}
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export function LocationsPanel({
  canReadLocations,
  canSelectTerritory,
  isSingleTerritoryImposed,
  onBack,
  onClose,
  territoryId,
  territoryName,
}: LocationsPanelProps): React.JSX.Element {
  const { t } = useTranslation('common');
  const { selectTerritory, siteId: currentLocationId, territoryId: currentTerritoryId } = useMasterContext();
  const { data } = useLocations({
    enabled: !!territoryId && canReadLocations,
    page: 1,
    pageSize: 50,
    territoryIds: territoryId ? [territoryId] : [],
  });

  const locations = data?.items ?? [];
  const total = data?.total ?? 0;

  // Territory is "active" only when it's the current territory AND no location is selected
  const isTerritoryOnlySelected = territoryId === currentTerritoryId && !currentLocationId;

  const handleTerritorySelect = (): void => {
    if (territoryId) {
      selectTerritory(territoryId);
      onClose();
    }
  };

  const handleTerritorySelectKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleTerritorySelect();
    }
  };

  return (
    <div>
      {territoryName && (
        <TerritoryHeader
          canSelectTerritory={canSelectTerritory}
          isSelected={isTerritoryOnlySelected}
          isSingleTerritoryImposed={isSingleTerritoryImposed}
          onBack={onBack}
          onSelect={handleTerritorySelect}
          onSelectKeyDown={handleTerritorySelectKeyDown}
          territoryName={territoryName}
        />
      )}

      <div className="px-3 py-1.5">
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
          {t('mainSelector.locations')} ({total})
        </span>
      </div>

      <div>
        <div className="p-1">
          {territoryId &&
            locations.map((location) => (
              <LocationRow key={location.id} location={location} onClose={onClose} territoryId={territoryId} />
            ))}
        </div>
      </div>
    </div>
  );
}
